// src/pages/public/StorefrontPage.jsx
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Star, MapPin, Globe, Instagram, MessageCircle, Package, Calendar } from 'lucide-react'
import { storefrontApi } from '../../api/services'
import { Button, PageSpinner, Badge } from '../../components/common/ui'

export default function StorefrontPage() {
  const { slug } = useParams()
  const [tab, setTab] = useState('products')

  const { data, isLoading } = useQuery({
    queryKey: ['storefront', slug],
    queryFn: () => storefrontApi.show(slug),
  })

  if (isLoading) return <PageSpinner />
  const payload = data?.data?.data || data?.data
  if (!payload) return <div className="py-24 text-center text-muted">Storefront not found.</div>

  const { storefront, products, reviews, stats } = payload
  const productList = products?.data || []

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="relative rounded-2xl overflow-hidden mb-6 border border-border">
        <div className="h-48 bg-gradient-brand-soft">
          {storefront.banner_image && <img src={storefront.banner_image} alt="" className="w-full h-full object-cover opacity-70" />}
        </div>
        <div className="px-6 pb-6 pt-4 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {storefront.logo_image && (
              <div className="-mt-12 w-20 h-20 rounded-2xl overflow-hidden border-4 border-white shadow flex-shrink-0">
                <img src={storefront.logo_image} alt={storefront.display_name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-display font-bold text-gray-900">{storefront.display_name}</h1>
                {storefront.gi_certified && <Badge variant="green">GI Certified ✓</Badge>}
                {storefront.verified_exporter && <Badge variant="blue">Verified Exporter</Badge>}
              </div>
              {storefront.city && <p className="text-sm text-muted flex items-center gap-1 mt-1"><MapPin size={13} />{storefront.city}{storefront.state ? `, ${storefront.state}` : ''}</p>}
              {storefront.tagline && <p className="text-sm text-gray-600 mt-1">{storefront.tagline}</p>}
            </div>
            <div className="flex gap-2">
              <Link to={`/messages/new?supplier_id=${storefront.supplier_id}`}>
                <Button variant="secondary" size="sm"><MessageCircle size={14} /> Contact</Button>
              </Link>
              {storefront.website && <a href={storefront.website} target="_blank" rel="noreferrer"><Button variant="secondary" size="sm"><Globe size={14} /></Button></a>}
              {storefront.instagram_url && <a href={storefront.instagram_url} target="_blank" rel="noreferrer"><Button variant="secondary" size="sm"><Instagram size={14} /></Button></a>}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Products', value: stats?.total_products ?? productList.length, icon: <Package size={16} /> },
          { label: 'Avg Rating', value: stats?.avg_rating ? Number(stats.avg_rating).toFixed(1) + ' ★' : '—', icon: <Star size={16} /> },
          { label: 'Reviews', value: stats?.total_reviews ?? 0, icon: null },
          { label: 'Member Since', value: stats?.member_since || storefront.supplier?.created_at?.slice(0,7), icon: <Calendar size={16} /> },
        ].map(({ label, value, icon }) => (
          <div key={label} className="bg-white border border-border rounded-xl p-4 text-center">
            {icon && <div className="text-pink mx-auto mb-1 flex justify-center">{icon}</div>}
            <div className="text-xl font-bold text-gray-900">{value}</div>
            <div className="text-xs text-muted">{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6">
        {['products', 'about', 'reviews'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-3 text-sm font-bold capitalize border-b-2 -mb-px transition-colors ${tab === t ? 'border-pink text-pink' : 'border-transparent text-muted'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'products' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {productList.length === 0 && <p className="text-sm text-muted col-span-4 py-8 text-center">No products listed yet.</p>}
          {productList.map(p => (
            <Link key={p.id} to={`/fabrics/${p.slug}`} className="group">
              <div className="aspect-square rounded-xl overflow-hidden bg-surface border border-border mb-2 group-hover:border-pink transition-colors">
                <img src={p.primary_image?.url || p.primary_image_url} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <p className="text-xs font-bold text-gray-900 line-clamp-2">{p.name}</p>
              <p className="text-xs text-pink font-bold mt-0.5">₹{Number(p.retail_price || p.price).toLocaleString('en-IN')}/m</p>
            </Link>
          ))}
        </div>
      )}

      {tab === 'about' && (
        <div className="max-w-2xl space-y-4">
          {storefront.about && <p className="text-sm text-gray-600 leading-relaxed">{storefront.about}</p>}
          {storefront.specialities && (
            <div>
              <h3 className="font-bold text-sm text-gray-900 mb-2">Specialties</h3>
              <p className="text-sm text-gray-600">{storefront.specialities}</p>
            </div>
          )}
          {storefront.certifications && (
            <div>
              <h3 className="font-bold text-sm text-gray-900 mb-2">Certifications</h3>
              <p className="text-sm text-gray-600">{storefront.certifications}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-4">
          {(!reviews || reviews.length === 0) && <p className="text-sm text-muted py-8 text-center">No reviews yet.</p>}
          {reviews?.map(r => (
            <div key={r.id} className="bg-white border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} size={12} className={s <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} />)}</div>
                <span className="text-xs text-muted">{r.buyer?.name || 'Buyer'}</span>
              </div>
              {r.comment && <p className="text-sm text-gray-600">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
